import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useLogs } from '../api/queries';
import type { ActivityLogEntry, LogLevel, LogListParams, LogSource } from '../api/types';
import { STATUS } from '../charts/palette';
import { Card } from '../components/Card';
import { Empty, Failed, Loading } from '../components/States';
import { count, relative, when } from '../utils/format';

const LEVEL_COLOR: Record<LogLevel, string> = {
  debug: '#898781',
  info: '#3987e5',
  warn: STATUS.warning,
  error: STATUS.critical,
};

const SOURCE_LABEL: Record<LogSource, string> = {
  feedSync: 'Feed import',
  crawlTick: 'Crawl tick',
  scan: 'Scan',
  pacing: 'Pacing',
  queue: 'Queue',
};

const WINDOWS: { label: string; minutes: number | undefined }[] = [
  { label: 'Last hour', minutes: 60 },
  { label: 'Last 24 h', minutes: 1440 },
  { label: 'Last 7 days', minutes: 10080 },
  { label: 'All', minutes: undefined },
];

function ContextCell({ context }: { context: string | null }) {
  const [open, setOpen] = useState(false);

  if (!context) {
    return <span className="muted">—</span>;
  }

  let pretty = context;

  try {
    pretty = JSON.stringify(JSON.parse(context), null, 2);
  } catch {
    // Not JSON: show whatever was stored rather than hiding it.
  }

  return (
    <>
      <button type="button" className="button" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Detail'}
      </button>
      {open && (
        <pre
          style={{
            margin: '8px 0 0',
            padding: 10,
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 11,
            maxWidth: 460,
            overflowX: 'auto',
            whiteSpace: 'pre',
          }}
        >
          {pretty}
        </pre>
      )}
    </>
  );
}

export function LogsPage() {
  const [level, setLevel] = useState<LogLevel | ''>('');
  const [source, setSource] = useState<LogSource | ''>('');
  const [search, setSearch] = useState('');
  const [sinceMinutes, setSinceMinutes] = useState<number | undefined>(1440);
  const [page, setPage] = useState(1);
  const [live, setLive] = useState(true);

  const params: LogListParams = {
    level: level || undefined,
    source: source || undefined,
    search: search || undefined,
    sinceMinutes,
    page,
    perPage: 100,
  };

  const logs = useLogs(params, live ? 10000 : false);

  const total = logs.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / 100));

  function reset() {
    setLevel('');
    setSource('');
    setSearch('');
    setSinceMinutes(1440);
    setPage(1);
  }

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">Activity log</h1>
          <p className="page__lede">
            Everything the feed import and the crawler did, newest first — each search, each match decision,
            each block and each failure, with the numbers behind it. This is the same trail the process log
            prints, kept in the database so you can debug here instead of in MySQL.
          </p>
        </div>
        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={live} onChange={(event) => setLive(event.target.checked)} />
          <span className="secondary">Auto-refresh</span>
        </label>
      </header>

      <Card>
        <div className="filters">
          <div className="field" style={{ flex: '1 1 240px' }}>
            <label className="field__label" htmlFor="log-search">
              Search message or detail
            </label>
            <input
              id="log-search"
              className="control"
              style={{ width: '100%' }}
              placeholder="blocked, MLPARTS, no confident match…"
              defaultValue={search}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  setPage(1);
                  setSearch((event.target as HTMLInputElement).value);
                }
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="log-level">
              Minimum level
            </label>
            <select
              id="log-level"
              className="control"
              value={level}
              onChange={(event) => {
                setPage(1);
                setLevel(event.target.value as LogLevel | '');
              }}
            >
              <option value="">Everything</option>
              <option value="debug">Debug and worse</option>
              <option value="info">Info and worse</option>
              <option value="warn">Warnings and errors</option>
              <option value="error">Errors only</option>
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="log-source">
              Source
            </label>
            <select
              id="log-source"
              className="control"
              value={source}
              onChange={(event) => {
                setPage(1);
                setSource(event.target.value as LogSource | '');
              }}
            >
              <option value="">Any</option>
              {(Object.keys(SOURCE_LABEL) as LogSource[]).map((key) => (
                <option key={key} value={key}>
                  {SOURCE_LABEL[key]}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <span className="field__label">Window</span>
            <div className="row" style={{ gap: 6 }}>
              {WINDOWS.map((window) => (
                <button
                  key={window.label}
                  type="button"
                  className={`button${sinceMinutes === window.minutes ? ' button--primary' : ''}`}
                  style={{ padding: '7px 10px', fontSize: 12 }}
                  onClick={() => {
                    setPage(1);
                    setSinceMinutes(window.minutes);
                  }}
                >
                  {window.label}
                </button>
              ))}
            </div>
          </div>

          <button type="button" className="button" onClick={reset}>
            Reset
          </button>
        </div>
      </Card>

      {logs.isError ? (
        <Failed error={logs.error} />
      ) : logs.isLoading ? (
        <Loading label="Loading activity…" />
      ) : total === 0 ? (
        <Empty>
          <div>Nothing logged in this window.</div>
          <div className="muted">
            Run an import or a scan from the <Link to="/crawler">Crawler</Link> page and it will appear here.
          </div>
        </Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Level</th>
                  <th>Source</th>
                  <th>Event</th>
                  <th>Message</th>
                  <th className="num">Took</th>
                  <th>Product</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {logs.data?.items.map((entry: ActivityLogEntry) => (
                  <tr key={entry.id}>
                    <td className="secondary" title={when(entry.createdAt)}>
                      {relative(entry.createdAt)}
                    </td>
                    <td>
                      <span className="badge">
                        <span className="badge__dot" style={{ background: LEVEL_COLOR[entry.level] }} />
                        {entry.level}
                      </span>
                    </td>
                    <td className="secondary">{SOURCE_LABEL[entry.source] ?? entry.source}</td>
                    <td className="muted" style={{ fontSize: 11 }}>
                      {entry.event}
                    </td>
                    <td className="log__message">
                      <span title={entry.message}>{entry.message}</span>
                    </td>
                    <td className="num secondary">
                      {entry.durationMs === null ? '—' : `${(entry.durationMs / 1000).toFixed(1)} s`}
                    </td>
                    <td>
                      {entry.shopProductId === null ? (
                        <span className="muted">—</span>
                      ) : (
                        <Link to={`/products/${entry.shopProductId}`}>#{entry.shopProductId}</Link>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'normal' }}>
                      <ContextCell context={entry.context} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <span className="muted">
              {count(total)} entries · page {page} of {lastPage}
            </span>
            <div className="row">
              <button type="button" className="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ← Newer
              </button>
              <button
                type="button"
                className="button"
                disabled={page >= lastPage}
                onClick={() => setPage(page + 1)}
              >
                Older →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
